import { useEffect, useState, type ReactNode } from "react";
import { useGetUser } from "@workspace/api-client-react";
import { AuthContext } from "./auth-context";
import type { User } from "@workspace/api-client-react";

const API_BASE = "/api";
const DEVICE_ID_KEY = "rekinder:device_id";

export function getWebDeviceId(): string {
  const stored = localStorage.getItem(DEVICE_ID_KEY);
  if (stored) return stored;
  const generated =
    "web-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 11);
  localStorage.setItem(DEVICE_ID_KEY, generated);
  return generated;
}

export function getWebAuthHeaders(): Record<string, string> {
  return { "X-Device-ID": getWebDeviceId() };
}

async function apiPost<T>(path: string, body: object): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? "Request failed");
  return data as T;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [userId, setUserId] = useState<number | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: user, isLoading: isUserLoading } = useGetUser(userId as number, {
    query: {
      enabled: !!userId,
      retry: false,
    } as any,
  });

  useEffect(() => {
    const storedId = localStorage.getItem("rekinder_user_id");
    if (storedId) {
      setUserId(parseInt(storedId, 10));
    }
    setIsInitializing(false);
  }, []);

  const persistUser = (u: User) => {
    setUserId(u.id);
    localStorage.setItem("rekinder_user_id", u.id.toString());
  };

  const login = async (email: string, password: string) => {
    const u = await apiPost<User>("/auth/login", {
      email,
      password,
      deviceId: getWebDeviceId(),
      clientPlatform: "web",
      pushHandoff: "not_applicable",
    });
    persistUser(u);
  };

  const register = async (name: string, email: string, password: string) => {
    const result = await apiPost<User | { pendingConfirmation: true; email: string; name: string }>(
      "/auth/register",
      {
        name,
        email,
        password,
        deviceId: getWebDeviceId(),
        clientPlatform: "web",
        pushHandoff: "not_applicable",
      },
    );
    if ("pendingConfirmation" in result) {
      // The account was created but needs email confirmation before a
      // session can start — there is no user to persist yet.
      throw new Error(
        `We've sent a confirmation link to ${result.email}. Please check your inbox and confirm your email before signing in.`,
      );
    }
    persistUser(result);
  };

  // Sign-out ends the session on the server too: the browser's device grant
  // is revoked, so its device ID no longer authenticates API requests. If the
  // server does not confirm, the user stays signed in (as in the app) and the
  // caller says so; a grant that is already gone (401) counts as signed out.
  const logout = async () => {
    const res = await fetch(`${API_BASE}/auth/logout`, {
      method: "POST",
      headers: getWebAuthHeaders(),
    });
    if (!res.ok && res.status !== 401) throw new Error("Sign-out failed");
    setUserId(null);
    localStorage.removeItem("rekinder_user_id");
  };

  const sessionUser: User | null = user
    ? {
        id: user.id,
        name: user.displayName,
        avatarUrl: user.avatarUrl,
        location: null,
        bio: null,
        createdAt: user.joinedAt,
        listingsCount: 0,
        salesCount: user.salesCount,
        avgRating: user.rating,
        ratingCount: 0,
      }
    : null;

  return (
    <AuthContext.Provider
      value={{
        user: sessionUser,
        isLoading: isInitializing || (!!userId && isUserLoading),
        login,
        register,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
